import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, Building2, RefreshCw, Save } from "lucide-react";

const DEFAULT_PROFILE = {
  legal_name: "",
  business_type: "fnb",
  business_scale: "simple",
  address: "",
  phone: "",
  email: "",
  country: "ID",
  currency: "IDR",
  timezone: "Asia/Jakarta",
  date_format: "DD/MM/YYYY",
  fiscal_year_start: "01-01",
};

const BUSINESS_TYPES = [
  ["fnb", "Food & Beverage"],
  ["retail", "Retail"],
  ["manufacturing", "Manufaktur"],
  ["services", "Jasa"],
  ["distribution", "Distribusi"],
  ["logistics", "Logistik"],
];

const BUSINESS_SCALES = [
  ["simple", "Simple — usaha sederhana"],
  ["growing", "Growing — usaha berkembang"],
  ["enterprise", "Enterprise — perusahaan kompleks"],
];

export default function CompanyProfilePage() {
  const [tenant, setTenant] = useState(null);
  const [profile, setProfile] = useState(DEFAULT_PROFILE);
  const [storedProfile, setStoredProfile] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [tenantRes, configRes] = await Promise.all([
        api.get("/tenant/current"),
        api.get("/configuration?scope_type=tenant"),
      ]);

      const tenantData = tenantRes.data?.tenant;
      if (!tenantData?.id) {
        throw new Error("Identitas tenant tidak ditemukan");
      }

      const setting = (configRes.data?.settings || [])
        .find(item => item.key === "company.profile");

      const saved =
        setting?.value &&
        typeof setting.value === "object" &&
        !Array.isArray(setting.value)
          ? setting.value
          : {};

      setTenant(tenantData);
      setStoredProfile(saved);
      setProfile({ ...DEFAULT_PROFILE, ...saved });
    } catch (e) {
      toast.error(
        e?.response?.data?.detail ||
        e.message ||
        "Gagal memuat profil perusahaan"
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  function updateField(key, value) {
    setProfile(current => ({ ...current, [key]: value }));
  }

  async function save() {
    if (!tenant?.id) {
      toast.error("Identitas tenant belum tersedia");
      return;
    }

    setSaving(true);
    try {
      const value = { ...storedProfile, ...profile };

      await api.put("/configuration/settings", {
        key: "company.profile",
        value,
        scope_type: "tenant",
        active: true,
        description: "Metadata profil dan preferensi dasar perusahaan",
      });

      setStoredProfile(value);
      toast.success("Profil perusahaan berhasil disimpan");
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || "Gagal menyimpan profil perusahaan"
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="page-shell space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <Link
            to="/company-setup"
            className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 mb-3"
          >
            <ArrowLeft className="w-4 h-4" />
            Kembali ke Company Setup
          </Link>
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-orange-100 text-orange-600 flex items-center justify-center">
              <Building2 className="w-5 h-5" />
            </div>
            <div>
              <h1 className="text-2xl font-extrabold font-display">
                Profil Perusahaan
              </h1>
              <p className="text-sm text-slate-500">
                Identitas, model usaha, dan preferensi dasar perusahaan.
              </p>
            </div>
          </div>
        </div>
        <Button variant="outline" onClick={load} disabled={loading || saving}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Muat Ulang
        </Button>
      </div>

      {loading ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-slate-500">
            Memuat profil perusahaan...
          </CardContent>
        </Card>
      ) : (
        <>
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Identitas Tenant</CardTitle>
            </CardHeader>
            <CardContent className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Nama perusahaan</Label>
                <Input value={tenant?.name || ""} readOnly disabled />
                <p className="text-xs text-slate-500 mt-1">
                  Nama utama berasal dari data tenant dan belum dapat diubah
                  melalui halaman ini.
                </p>
              </div>
              <div>
                <Label>ID Tenant</Label>
                <Input value={tenant?.id || ""} readOnly disabled />
              </div>
              <div>
                <Label>Status tenant</Label>
                <Input value={tenant?.status || "-"} readOnly disabled />
              </div>
              <div>
                <Label>Slug</Label>
                <Input value={tenant?.slug || "-"} readOnly disabled />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Informasi Legal & Kontak</CardTitle>
            </CardHeader>
            <CardContent className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="legal_name">Nama legal perusahaan</Label>
                <Input
                  id="legal_name"
                  value={profile.legal_name}
                  onChange={e => updateField("legal_name", e.target.value)}
                  placeholder="Nama sesuai dokumen legal"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="company_email">Email perusahaan</Label>
                <Input
                  id="company_email"
                  type="email"
                  value={profile.email}
                  onChange={e => updateField("email", e.target.value)}
                  placeholder="email@perusahaan.com"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="company_phone">Nomor telepon</Label>
                <Input
                  id="company_phone"
                  value={profile.phone}
                  onChange={e => updateField("phone", e.target.value)}
                  placeholder="Nomor telepon perusahaan"
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="company_address">Alamat perusahaan</Label>
                <Input
                  id="company_address"
                  value={profile.address}
                  onChange={e => updateField("address", e.target.value)}
                  placeholder="Alamat lengkap perusahaan"
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Model Usaha</CardTitle>
            </CardHeader>
            <CardContent className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Jenis usaha</Label>
                <Select
                  value={profile.business_type}
                  onValueChange={v => updateField("business_type", v)}
                >
                  <SelectTrigger><SelectValue placeholder="Pilih jenis usaha" /></SelectTrigger>
                  <SelectContent>
                    {BUSINESS_TYPES.map(([value, label]) => (
                      <SelectItem key={value} value={value}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Skala usaha</Label>
                <Select
                  value={profile.business_scale}
                  onValueChange={v => updateField("business_scale", v)}
                >
                  <SelectTrigger><SelectValue placeholder="Pilih skala usaha" /></SelectTrigger>
                  <SelectContent>
                    {BUSINESS_SCALES.map(([value, label]) => (
                      <SelectItem key={value} value={value}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Regional & Preferensi Akuntansi</CardTitle>
            </CardHeader>
            <CardContent className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Negara</Label>
                <Select value={profile.country} onValueChange={v => updateField("country", v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ID">Indonesia</SelectItem>
                    <SelectItem value="SG">Singapura</SelectItem>
                    <SelectItem value="MY">Malaysia</SelectItem>
                    <SelectItem value="US">Amerika Serikat</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Mata uang</Label>
                <Select value={profile.currency} onValueChange={v => updateField("currency", v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="IDR">IDR — Rupiah</SelectItem>
                    <SelectItem value="SGD">SGD — Dolar Singapura</SelectItem>
                    <SelectItem value="MYR">MYR — Ringgit</SelectItem>
                    <SelectItem value="USD">USD — Dolar AS</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Zona waktu</Label>
                <Select value={profile.timezone} onValueChange={v => updateField("timezone", v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Asia/Jakarta">WIB — Asia/Jakarta</SelectItem>
                    <SelectItem value="Asia/Makassar">WITA — Asia/Makassar</SelectItem>
                    <SelectItem value="Asia/Jayapura">WIT — Asia/Jayapura</SelectItem>
                    <SelectItem value="Asia/Singapore">Asia/Singapore</SelectItem>
                    <SelectItem value="UTC">UTC</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Format tanggal</Label>
                <Select value={profile.date_format} onValueChange={v => updateField("date_format", v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="DD/MM/YYYY">DD/MM/YYYY</SelectItem>
                    <SelectItem value="MM/DD/YYYY">MM/DD/YYYY</SelectItem>
                    <SelectItem value="YYYY-MM-DD">YYYY-MM-DD</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Awal tahun fiskal</Label>
                <Select
                  value={profile.fiscal_year_start}
                  onValueChange={v => updateField("fiscal_year_start", v)}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: 12 }, (_, i) => {
                      const month = String(i + 1).padStart(2, "0");
                      const names = [
                        "Januari", "Februari", "Maret", "April", "Mei", "Juni",
                        "Juli", "Agustus", "September", "Oktober", "November", "Desember"
                      ];
                      return (
                        <SelectItem key={month} value={`${month}-01`}>
                          {names[i]}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
            Preferensi regional dan akuntansi pada halaman ini disimpan sebagai
            metadata profil. Pengaturan ini belum mengubah mata uang transaksi,
            zona waktu sistem, format seluruh laporan, atau periode akuntansi.
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" asChild>
              <Link to="/company-setup">Batal</Link>
            </Button>
            <Button onClick={save} disabled={saving || loading || !tenant?.id}
              className="bg-orange-500 hover:bg-orange-600">
              <Save className="w-4 h-4 mr-2" />
              {saving ? "Menyimpan..." : "Simpan Profil"}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
